import {
  Box,
  Flex,
  Text,
  Checkbox,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import Field from "ui/components/Input/Field";
import InputMask from "ui/components/Input/InputMask/index";
import DefaultButton from "ui/components/Button/DefaultButton";
import colors from "core/resources/theme/colors";
import { BiMailSend } from "react-icons/bi";
import { useMutation } from "react-query";
import { showSuccess } from "infra/packages/helpers/alerta";
import { useNavigate } from "react-router-dom";
import { isValidCPF } from "@brazilian-utils/brazilian-utils";
import { useRegistroPcd } from "core/features/registro-pcd/hooks/useRegistroPcd";
import { useState } from "react";
import DatePicker from "ui/components/Input/DatePicker";
import Swal from "sweetalert2";
import useIsLoggedIn from "core/features/auth/hooks/useIsLoggedIn";
import FileUpload from "ui/components/Input/FileUpload";
import useGetDiasUteis from "core/features/eventos/hooks/useGetDiasUteis";
import Calendar from "ui/components/Calendar/Calendar";
import { DiaUtil } from "core/features/ingresso/typings";

export default function FormRegistroPcd() {
  const { control, handleSubmit, reset } = useForm<any>();
  const navigate = useNavigate();
  const isLogged = useIsLoggedIn();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [hasResponsible, setHasResponsible] = useState(false);
  const [selectedCalendarioId, setSelectedCalendarioId] = useState<string>("");
  const { data: diasUteis } = useGetDiasUteis();

  console.log("Dias úteis carregados:", diasUteis);

  const { mutate, isLoading } = useMutation(useRegistroPcd, {
    onSuccess() {
      showSuccess("Registro PCD enviado com sucesso");
      reset({});
      setSelectedFile(null);
      navigate("/");
    },
  });

  const onSubmit = (form: any) => {
    console.log("=== INICIO DO SUBMIT ===");
    console.log("selectedCalendarioId:", selectedCalendarioId);
    console.log("Form data:", form);

    if (!isLogged) {
      return Swal.fire(
        "Usuário não encontrado",
        "Você precisa fazer login para realizar esta operação",
        "warning"
      );
    }

    if (!selectedCalendarioId) {
      return Swal.fire(
        "Data de visita obrigatória",
        "Selecione uma data de visita no calendário",
        "warning"
      );
    }

    if (hasResponsible && (!form.responsavelNome || !form.responsavelDocumento || !form.responsavelTelefone)) {
      return Swal.fire(
        "Dados do acompanhante obrigatórios",
        "Preencha todos os campos do acompanhante ou responsável",
        "warning"
      );
    }

    if (!selectedFile) {
      return Swal.fire(
        "Arquivo obrigatório",
        "É necessário anexar o comprovante ou atestado de PCD",
        "warning"
      );
    }

    const formData = new FormData();
    formData.append("TipoId", "1");
    formData.append("Nome", form.nome);
    formData.append("DataNascimento", form.dataNascimento.toISOString().split("T")[0]);
    formData.append("Documento", form.documento);
    formData.append("NacionalidadeId", "1");
    formData.append("Telefone", form.telefone);
    formData.append("TipoDocumentoId", "4");
    formData.append("Arquivo", selectedFile);
    formData.append("CalendarioVisitaId", selectedCalendarioId);

    console.log("=== FORMDATA ===");
    console.log("CalendarioVisitaId no FormData:", selectedCalendarioId);
    formData.forEach((value, key) => {
      console.log(key + ": " + value);
    });

    if (form.responsavelNome) {
      formData.append("ResponsavelNome", form.responsavelNome);
    }
    if (form.responsavelDocumento) {
      formData.append("ResponsavelDocumento", form.responsavelDocumento);
    }
    if (form.responsavelTelefone) {
      formData.append("ResponsavelTelefone", form.responsavelTelefone);
    }

    mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex
        flexDir="column"
        w={["100%", "100%", "100%", "600px", "1000px"]}
        gap={6}
      >
        <Box>
          <Text fontWeight="medium" fontSize="14px" mb={2} color={colors.purple500}>
            Nome completo
          </Text>
          <Field
            control={control}
            name="nome"
            inputProps={{ placeholder: "Nome completo*" }}
            rules={{
              required: "Informe seu nome completo",
              validate: (value) => {
                const words = value.trim().split(" ");
                return words.length >= 2 || "Informe nome e sobrenome";
              }
            }}
          />
        </Box>

        <Flex
          flexDir={["column", "row"]}
          justifyContent="space-between"
          gap={4}
        >
          <Box w="100%">
            <Text fontWeight="medium" fontSize="14px" mb={2} color={colors.purple500}>
              Data de Nascimento
            </Text>
            <Field
              control={control}
              name="dataNascimento"
              rules={{
                required: "Informe a data de nascimento",
              }}
              render={({ field }) => (
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selectedDate={field.value}
                  onChange={(date: Date) => field.onChange(date)}
                />
              )}
            />
          </Box>
          <Box w="100%">
            <Text fontWeight="medium" fontSize="14px" mb={2} color={colors.purple500}>
              CPF ou Passaporte
            </Text>
            <Field
              control={control}
              name="documento"
              inputProps={{ placeholder: "CPF ou Passaporte*" }}
              rules={{
                required: "Campo obrigatório",
                validate: (v) => {
                  if (v.length === 11 || v.replace(/\D/g, '').length === 11) {
                    return isValidCPF(v) || "CPF inválido";
                  }
                  return v.length >= 6 || "Documento inválido";
                }
              }}
              render={({ field, isInvalid }) => (
                <InputMask
                  {...field}
                  isInvalid={isInvalid}
                  mask="999.999.999-99"
                  placeholder="CPF ou Passaporte*"
                />
              )}
            />
          </Box>
        </Flex>

        <Box>
          <Text fontWeight="medium" fontSize="14px" mb={2} color={colors.purple500}>
            Telefone
          </Text>
          <Field
            control={control}
            name="telefone"
            rules={{
              required: "Telefone obrigatório",
            }}
            render={({ field }) => (
              <InputMask
                {...field}
                placeholder="Telefone*"
                mask="(99) 99999-9999"
              />
            )}
          />
        </Box>

        <Box>
          <Text fontWeight="medium" fontSize="16px" mb={4} color={colors.purple500}>
            Data de Visita*
          </Text>
          {selectedCalendarioId && (
            <Text fontSize="14px" color={colors.green} mb={2}>
              ✔ Data selecionada
            </Text>
          )}
          <Calendar
            widthCalendar={["360px", "360px", "360px", "550px"]}
            heightCalendar={["355px", "355px", "355px", "510px"]}
            widthCalendarDay={["44px", "44px", "44px", "70px"]}
            heightCalendarDay={["44px", "44px", "44px", "70px"]}
            gapSemanas={["23px", "23px", "23px", "48px"]}
            mlSemanas={["8px", "8px", "8px", "20px"]}
            pendingXCalendar={[1, 1, 1, 2]}
            fontSizeStatusDay={["7px", "7px", "7px", "14px"]}
            daysOpen={diasUteis || []}
            hideValue={true}
            onChangeDiaUtil={(diaUtil?: DiaUtil) => {
              if (diaUtil?.calendarioId) {
                console.log("Data selecionada:", diaUtil);
                console.log("CalendarioId:", diaUtil.calendarioId);
                setSelectedCalendarioId(diaUtil.calendarioId);
              } else {
                console.log("Nenhum calendarioId encontrado", diaUtil);
              }
            }}
          />
        </Box>

        <Box>
          <Checkbox
            isChecked={hasResponsible}
            onChange={(e) => setHasResponsible(e.target.checked)}
            colorScheme="purple"
            size="md"
          >
            <Text fontWeight="medium" fontSize="16px" color={colors.purple500}>
              Você terá um Acompanhante ou Responsável?
            </Text>
          </Checkbox>
        </Box>

        {hasResponsible && (
          <>
            <Text fontWeight="medium" fontSize="16px" color={colors.purple500}>
              Dados do Acompanhante ou Responsável
            </Text>

            <Box>
              <Text fontWeight="medium" fontSize="14px" mb={2} color={colors.purple500}>
                Nome do acompanhante ou responsável
              </Text>
              <Field
                control={control}
                name="responsavelNome"
                inputProps={{ placeholder: "Nome do acompanhante ou responsável*" }}
                rules={{
                  required: hasResponsible ? "Campo obrigatório" : false,
                }}
              />
            </Box>

            <Flex
              flexDir={["column", "row"]}
              justifyContent="space-between"
              gap={4}
            >
              <Box w="100%">
                <Text fontWeight="medium" fontSize="14px" mb={2} color={colors.purple500}>
                  Documento do acompanhante ou responsável
                </Text>
                <Field
                  control={control}
                  name="responsavelDocumento"
                  inputProps={{ placeholder: "Documento do acompanhante ou responsável*" }}
                  rules={{
                    required: hasResponsible ? "Campo obrigatório" : false,
                  }}
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      mask="999.999.999-99"
                      placeholder="Documento do acompanhante ou responsável*"
                    />
                  )}
                />
              </Box>
              <Box w="100%">
                <Text fontWeight="medium" fontSize="14px" mb={2} color={colors.purple500}>
                  Telefone do acompanhante ou responsável
                </Text>
                <Field
                  control={control}
                  name="responsavelTelefone"
                  rules={{
                    required: hasResponsible ? "Campo obrigatório" : false,
                  }}
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      placeholder="Telefone do acompanhante ou responsável*"
                      mask="(99) 99999-9999"
                    />
                  )}
                />
              </Box>
            </Flex>
          </>
        )}

        <Box>
          <Text fontWeight="medium" fontSize="16px" mb={4} color={colors.purple500}>
            Aceitamos os seguintes documentos:
          </Text>
          <Box mb={4} pl={4}>
            <Text fontSize="14px" mb={1}>✔ RG com CID</Text>
            <Text fontSize="14px" mb={1}>✔ Carteira PcD</Text>
            <Text fontSize="14px" mb={1}>✔ Passe Livre PcD</Text>
            <Text fontSize="14px" mb={1}>✔ Laudo médico contendo CID</Text>
            <Text fontSize="14px" mb={1}>✔ BPC/LOAS (deficiência permanente)</Text>
            <Text fontSize="14px" mb={1}>✔ CNH Especial (somente válida acompanhada de laudo)</Text>
            <Text fontSize="14px" mb={1}>✔ Cartão de estacionamento PcD</Text>
          </Box>
          <Text fontWeight="medium" fontSize="14px" mb={2}>
            Comprovante ou Atestado de PCD*
          </Text>
          <FileUpload
            onFileSelect={setSelectedFile}
            selectedFile={selectedFile}
          />
        </Box>

        <Flex
          w="100%"
          flexDir="column"
          gap={4}
          textAlign="center"
          justify="center"
        >
          <Text fontSize="1rem">
            Após o envio do formulário, entraremos em contato para validar as informações.
          </Text>

          <DefaultButton
            isLoading={isLoading}
            type="submit"
            title="ENVIAR REGISTRO"
            bg={colors.newBlue}
            icon={<BiMailSend size={20} />}
          />
        </Flex>
      </Flex>
    </form>
  );
}